import React from 'react';
import { useTheme, THEMES } from '../../contexts/ThemeContext';
import {
  MenuItem,
  Select,
  FormControl,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';

const textStyle = {
  fontFamily: 'Inter',
  marginLeft: 2,
};

const ThemeSelector = ({ variant = 'select', onClose }) => {
  const { theme, changeTheme } = useTheme();

  const handleThemeChange = (newTheme) => {
    changeTheme(newTheme);
    // Don't close the menu - allow users to toggle between themes quickly
  };

  if (variant === 'menuItem') {
    return (
      <>
        <MenuItem 
          sx={{ 
            opacity: 1, 
            cursor: 'default', 
            py: 0.5,
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          onClick={(e) => {
            // Prevent menu from closing and prevent selection
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <PaletteIcon sx={{ mr: 2, color: 'text.primary' }} />
          <Typography variant="body2" sx={{ ...textStyle, color: 'text.primary' }}>
            Theme
          </Typography>
        </MenuItem>
        <MenuItem 
          sx={{ 
            opacity: 1, 
            cursor: 'default', 
            py: 0.5, 
            px: 2,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          onClick={(e) => {
            // Prevent menu from closing when clicking on the toggle buttons
            e.stopPropagation();
          }}
        >
          <Box 
            sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            onClick={(e) => {
              // Prevent menu from closing
              e.stopPropagation();
            }}
          >
            <ToggleButtonGroup
              value={theme}
              exclusive
              onChange={(e, newTheme) => {
                e.stopPropagation();
                if (newTheme !== null) {
                  handleThemeChange(newTheme);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid',
                  borderColor: 'divider',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              }}
            >
              <ToggleButton value={THEMES.DARK} aria-label="dark">
                D
              </ToggleButton>
              <ToggleButton value={THEMES.LIGHT} aria-label="light">
                L
              </ToggleButton>
              <ToggleButton value={THEMES.OASIS} aria-label="oasis">
                O
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </MenuItem>
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PaletteIcon sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontFamily: 'Inter' }}>
            Theme
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={theme}
            exclusive
            onChange={(e, newTheme) => {
              if (newTheme !== null) {
                handleThemeChange(newTheme);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                minWidth: 'auto',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <ToggleButton value={THEMES.DARK} aria-label="dark">
              D
            </ToggleButton>
            <ToggleButton value={THEMES.LIGHT} aria-label="light">
              L
            </ToggleButton>
            <ToggleButton value={THEMES.OASIS} aria-label="oasis">
              O
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
    );
  }

  // Default variant: standalone select
  const handleSelectChange = (event) => {
    const newTheme = event.target.value;
    handleThemeChange(newTheme);
  };

  return (
    <FormControl size="small" sx={{ minWidth: 100 }}>
      <Select
        value={theme}
        onChange={handleSelectChange}
        displayEmpty
        sx={{
          color: 'inherit',
          fontSize: '0.875rem',
          height: '36px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
        }}
      >
        <MenuItem value={THEMES.DARK}>Dark</MenuItem>
        <MenuItem value={THEMES.LIGHT}>Light</MenuItem>
        <MenuItem value={THEMES.OASIS}>Oasis</MenuItem>
      </Select>
    </FormControl>
  );
};

export default ThemeSelector;

